"use client";

import { useTranslations } from "next-intl";
import { Globe, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { locales, localeNames, localeToLanguage, type Locale } from "@/i18n/config";

interface StepLanguageProps {
  selected: string; // language name like "english", "hindi"
  onSelect: (language: string) => void;
  currentLocale: Locale;
}

// Group languages by region for better UX
const languageGroups = [
  {
    labelKey: "langGroupPopular",
    languages: ["en", "hi", "ta", "te", "kn"] as Locale[],
  },
  {
    labelKey: "langGroupMore",
    languages: ["ml", "mr", "bn", "gu", "pa", "or", "as"] as Locale[],
  },
];

export function StepLanguage({
  selected,
  onSelect,
  currentLocale,
}: StepLanguageProps) {
  const t = useTranslations("scan");

  // Find which locale code matches the selected language
  const selectedLocale = (Object.entries(localeToLanguage).find(
    ([, lang]) => lang === selected
  )?.[0] ?? "en") as Locale;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Globe size={15} className="text-brand" />
          {t("languageTitle")}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("languageDesc")}
        </p>
      </div>

      {languageGroups.map((group) => (
        <div key={group.labelKey} className="space-y-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            {t(group.labelKey)}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {group.languages.map((locale) => {
              const language = localeToLanguage[locale];
              const isSelected = selectedLocale === locale;
              const isCurrentUi = currentLocale === locale;

              return (
                <button
                  key={locale}
                  onClick={() => onSelect(language)}
                  className={cn(
                    "relative flex items-center gap-2.5 rounded-xl border p-3 text-left transition-all",
                    isSelected
                      ? "border-brand bg-brand/5 ring-1 ring-brand/30"
                      : "border-border bg-card hover:border-foreground/20 hover:bg-accent"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        "text-sm font-medium",
                        isSelected ? "text-foreground" : "text-foreground/80"
                      )}
                    >
                      {localeNames[locale]}
                    </p>
                    <p className="text-[10px] text-muted-foreground capitalize">
                      {language}
                    </p>
                  </div>
                  {isSelected && (
                    <Check
                      size={14}
                      className="text-brand shrink-0"
                    />
                  )}
                  {isCurrentUi && !isSelected && (
                    <span className="text-[8px] font-medium text-muted-foreground bg-muted rounded px-1 py-0.5 shrink-0">
                      {t("currentUi")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Bilingual note */}
      {selected !== "english" && (
        <div className="rounded-lg bg-blue-50/50 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-900/30 p-3">
          <p className="text-xs text-foreground/70">
            {t("bilingualNote", { language: selected })}
          </p>
        </div>
      )}
    </div>
  );
}
